import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

/**
 * Monthly + yearly overview shared by the Suggestions and Kaizen dashboards.
 *
 * Both registers answer the same three questions — how many were submitted, how
 * many people took part, and how the year is trending — so they render through
 * one component rather than two that drift apart. `fetcher` supplies the data
 * so the component stays ignorant of which collection it is describing.
 *
 * Participation is deliberately NOT the sum of the monthly figures: somebody who
 * filed in three separate months is one participant for the year, and the server
 * computes that distinctly.
 */
export default function ParticipationSummary({ fetcher, unit, noun, nounPlural, accent }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetcher({ year, unit });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not load the ${noun} summary`);
      setData(null);
    } finally { setLoading(false); }
  }, [fetcher, year, unit, noun]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const months = useMemo(() => data?.months || [], [data]);
  const peak = useMemo(() => Math.max(1, ...months.map((m) => m.total)), [months]);

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const set = new Set([...(data?.years || []), now, now - 1, now - 2]);
    return [...set].sort((a, b) => b - a);
  }, [data]);

  const stat = (label, value, sub, tone = 'text-[#11182B]') => (
    <div className="bg-white border border-[#E6E9F0] rounded-xl px-4 py-3 flex-1 min-w-[150px]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2]">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#5A6377] mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div data-testid={`${noun}-summary`} className="mb-6">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-sm font-semibold text-[#11182B]">
          {year} overview
          {data?.deadline_day && (
            <span className="ml-2 font-normal text-[11px] text-[#5A6377]">
              · each month closes on the {data.deadline_day}
              {data.deadline_day === 1 ? 'st' : data.deadline_day === 2 ? 'nd'
                : data.deadline_day === 3 ? 'rd' : 'th'} of the next month
            </span>
          )}
        </h2>
        <select data-testid={`${noun}-year`} value={year} aria-label="Year"
          onChange={(e) => setYear(Number(e.target.value))}
          className="border border-[#E6E9F0] rounded-md bg-white text-sm px-3 py-1.5">
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading && <div className="text-sm text-[#5A6377]">Loading…</div>}

      {!loading && t && (
        <>
          <div className="flex gap-3 mb-4 flex-wrap">
            {stat(`${nounPlural} this year`, t.total,
              t.total ? `${t.avg_per_month} per month on average` : 'none yet')}
            {stat('Employees who took part', t.employees,
              `of ${t.headcount} · ${t.participation}%`, 'text-[#15654B]')}
            {stat('Busiest month', t.best_month ? t.best_month.split(' ')[0] : '—',
              t.best_month ? `${t.best_month_total} ${t.best_month_total === 1 ? noun : nounPlural.toLowerCase()}` : null)}
            {stat('Submitted late', t.late,
              t.late ? 'after the 10th' : 'all on time',
              t.late ? 'text-[#B14A0E]' : 'text-[#11182B]')}
          </div>

          {t.unlinked > 0 && (
            <p data-testid={`${noun}-unlinked-note`} className="text-[11px] text-[#B14A0E] mb-3">
              {t.unlinked} {t.unlinked === 1 ? 'entry names' : 'entries name'} someone only as free
              text, so {t.unlinked === 1 ? 'it is' : 'they are'} counted in the total but not in
              participation. Pick the employee on those records to include them.
            </p>
          )}

          {/* Month-by-month. A plain bar per month reads faster here than a chart
              library, prints cleanly, and needs no colour to be understood. */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left">
                  <th className="pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2]">Month</th>
                  <th className="pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2] w-[45%]">{nounPlural}</th>
                  <th className="pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2] text-right">Count</th>
                  <th className="pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2] text-right">Employees</th>
                  <th className="pb-2 pr-6 text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2] text-right">Late</th>
                  <th className="pb-2 pl-2 text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2]">Window</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.period} data-testid={`${noun}-month-row`} className="border-t border-[#F2F4F8]">
                    <td className="py-1.5 text-[13px] text-[#11182B] whitespace-nowrap">{m.short}</td>
                    <td className="py-1.5 pr-4">
                      <div className="h-2 rounded-full bg-[#F2F4F8] overflow-hidden" aria-hidden="true">
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${(m.total / peak) * 100}%`, background: accent }} />
                      </div>
                    </td>
                    <td className="py-1.5 text-[13px] tabular-nums text-right text-[#11182B]">{m.total}</td>
                    <td className="py-1.5 text-[13px] tabular-nums text-right text-[#5A6377]">
                      {m.employees}{m.employees ? <span className="text-[#98A1B2]"> · {m.participation}%</span> : null}
                    </td>
                    <td className={`py-1.5 pr-6 text-[13px] tabular-nums text-right ${m.late ? 'text-[#B14A0E]' : 'text-[#98A1B2]'}`}>
                      {m.late || '—'}
                    </td>
                    <td className="py-1.5 pl-2 whitespace-nowrap">
                      {m.open
                        ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#E7F7EF] text-[#15654B]">
                            Open till {m.deadline_label}
                          </span>
                        : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F2F4F8] text-[#5A6377]">
                            Closed {m.deadline_label}
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
